import React, { useEffect, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import shuffle from 'lodash/shuffle';
import { useRouter } from 'next/router';
import { useQuery } from 'urql';

import AddToCartBlock from '@/components/AddToCartBlock';
import Breadcrumbs from '@/components/Breadcrumbs';
import GalleryCarousel from '@/components/GalleryCarousel';
import HeroImage from '@/components/layout/HeroImage';
import LocationCard from '@/components/LocationCard';
import OfferCard from '@/components/OfferCard';
import PlaceHolderImage from '@/components/PlaceHolderImage';
import ShareBlock from '@/components/ShareBlock';
import query from '@/graphql/queries/user.graphql';
import useIsMobile from '@/hooks/useIsMobile';
import useStore from '@/hooks/useStore';
import { trackImpression } from '@/lib/GA';
import { extractItems } from '@/lib/GraphQLHelper';

import CouponIcon from '@/images/icons/coupon.svg';

import relatedQuery from './related.graphql';

import styles from '../styles.module.scss';

interface Props {
  product: ETicketFragment;
  galleryImages?: ImageObject[];
}

const Product = ({ product, galleryImages }: Props) => {
  const [hasInit, setHasInit] = useState(false);
  const { exclusive, uuid } = useStore('userStore');
  const [isMobile] = useIsMobile(768);
  const router = useRouter();
  const productCategories = extractItems<ProductCategoryFragment>(product.productCategories);

  const [{ data, fetching }] = useQuery<UserQuery, UserQueryVariables>({
    query,
    variables: {
      uuid,
      productPage: true,
      slug: router.query.slug,
      exclusive,
    },
  });

  useEffect(() => {
    if (!fetching && !hasInit) {
      if (product) {
        trackImpression({
          id: product.id!,
          name: product.title!,
          category: extractItems<ProductCategoryFragment>(product.productCategories)![0]!.title!,
          price: product.defaultPrice!,
        });
        setHasInit(true);
      }
    }
  }, [product, fetching, hasInit]);

  useEffectOnce(() => {
    if (typeof window !== 'undefined') {
      document.body.setAttribute('data-static-bottom-padding-small', isMobile?.toString());
    }

    return () => {
      document.body.removeAttribute('data-static-bottom-padding-small');
    };
  });

  const interests =
    extractItems<InterestFragment>(product.relatedInterests)
      ?.map((i) => i?.slug)
      .toString() || '*';

  // Checks if item is already within in the wishlist
  const itemInWishlist = useMemo(
    () => !!data?.userDetails?.wishlist?.filter((w) => w?.uid === product.uid).length,
    [product?.uid, data?.userDetails?.wishlist],
  );

  const [{ data: relatedEntries }] = useQuery({
    query: relatedQuery,
    pause: fetching,
    // hide exclusive products if not logged in, don't show the page products based on slug
    // show eTickets with a matching category, show eTickets with matching interests
    variables: {
      exclusive,
      search: `-slug:${router?.query?.slug} ${
        router?.query?.category && `productCategories:${router?.query?.category}`
      } ${interests && `relatedInterests:${interests}`}`,
    },
  });

  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Online Shop', uri: '/shop' },
    {
      title: productCategories?.[0]?.title || '',
      uri: `/shop/${productCategories?.[0]?.slug}` || '',
    },
    { title: data?.product?.title || '' },
  ];

  const similarEntries = useMemo(() => {
    if (relatedEntries) {
      // prettier-ignore
      return shuffle(relatedEntries.products.map((e: ETicketFragment) => <OfferCard offer={e} key={e.slug} />)).slice(0, 3);
    }
    return [];
  }, [relatedEntries]);

  return (
    <>
      <div className={styles.product} data-testid="eTicketPage">
        <HeroImage image={product?.gallery?.[0]?.url} />
        <div className={styles.inner}>
          <h1>{product.title}</h1>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
          <p className={styles.offerDiscount}>
            {product.description && <CouponIcon />}
            {product.description}
          </p>
          <div className={styles.content}>
            <div className={styles.imageContainer}>
              {!!galleryImages?.length ? <GalleryCarousel images={galleryImages} /> : <PlaceHolderImage />}
            </div>
            <div className={styles.addToCart}>
              <AddToCartBlock product={product} inWishlist={itemInWishlist} />
            </div>
            {product?.body && <div className={styles.body} dangerouslySetInnerHTML={{ __html: product?.body }} />}
            {isMobile && <ShareBlock />}
          </div>
          {!!product?.locations?.length && (
            <div className={styles.locations}>
              <h3>Locations where {isMobile ? 'you can claim' : 'this offer can be claimed'}</h3>
              <div className={styles.entries}>
                {product?.locations?.map((location: Maybe<LocationFragment>) => (
                  <LocationCard key={location?.description} location={location!} />
                ))}
              </div>
            </div>
          )}
          {!!similarEntries.length && (
            <div className={styles.similar}>
              <h3>Similar items</h3>
              <div className={styles.entries}>{similarEntries.map((entry: JSX.Element) => entry)}</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Product;
